<script setup lang="ts">
  import { useFullscreen } from '@vueuse/core';
  import { parse } from 'papaparse';
  import { computed, onBeforeMount, ref, useTemplateRef } from 'vue';

  import { Button } from '@ui/components/button';
  import { Tooltip, TooltipContent, TooltipTrigger } from '@ui/components/tooltip';
  import { downloadFile } from '@ui/utils/utils';

  const props = defineProps<{ url: string; fileName: string; mimeType?: string }>();
  const csv_table_ref = useTemplateRef<HTMLElement>('csv_table');
  const DEFAULT_OPTION = 'text-2xs';

  const { toggle } = useFullscreen(csv_table_ref);
  const csvTableHtml = ref('');
  const csvTableError = ref(false);

  const expectedCSVTypes = ['text/csv', 'binary/octet-stream'];

  const options = ['text-3xs', 'text-2xs', 'text-xs', 'text-sm', 'text-base', 'text-lg', 'text-xl'];

  const selectedOption = ref(DEFAULT_OPTION);

  const selectedOptionIndex = computed(() => options.indexOf(selectedOption.value));

  const disableMinus = computed(() => {
    return selectedOptionIndex.value === 0;
  });

  const disablePlus = computed(() => {
    return selectedOptionIndex.value === options.length - 1;
  });

  const decreaseSize = () => {
    selectedOption.value = options[selectedOptionIndex.value - 1];
  };

  const increaseSize = () => {
    selectedOption.value = options[selectedOptionIndex.value + 1];
  };

  const downloadAttachment = () => {
    if (props.url) {
      downloadFile(props.url, props.fileName);
    }
  };

  const parseCsv = async () => {
    try {
      if (!props.url) {
        csvTableError.value = true;
        return;
      }

      const response = await fetch(props.url);
      const isValidFileType = expectedCSVTypes.includes(response.headers.get('Content-Type') || '');

      if (!isValidFileType) {
        csvTableError.value = true;
        return;
      }

      const csvText = await response.text();

      parse(csvText, {
        complete(results) {
          const rows = results.data;
          let html = '<table class="bg-background min-w-full text-left text-muted-foreground">';
          html += '<thead class="text-foreground capitalize sticky top-0 bg-background z-10"><tr>';
          rows[0].forEach((cell) => {
            html += `<th class="px-2 py-2 max-w-[200px]"><span class="truncate block" title="${cell}">${cell}</span></th>`;
          });
          html += '</tr></thead><tbody class="bg-background">';

          for (let i = 1; i < rows.length; i++) {
            if (rows[i].length === 1 && rows[i][0] === '') continue;
            html += '<tr class="bg-background border-t">';
            rows[i].forEach((cell) => {
              html += `<td class="px-2 py-2 max-w-[400px]"><span class="truncate block" title="${cell}">${cell}</span></td>`;
            });
            html += '</tr>';
          }

          html += '</tbody></table>';
          csvTableHtml.value = html;
        },
      });
    } catch (error) {
      console.error('Error parsing CSV:', error);
      csvTableError.value = true;
    }
  };

  onBeforeMount(() => {
    if (expectedCSVTypes.includes(props.mimeType || '')) {
      parseCsv();
    }
  });
</script>

<template>
  <div ref="csv_table" class="flex h-full flex-col">
    <div
      class="flex flex-nowrap items-center justify-between space-x-3 p-3 rounded-tr-sm rounded-tl-sm"
    >
      <div class="flex items-center gap-x-1">
        <Button variant="outline" size="icon" :disabled="disableMinus" @click="decreaseSize">
          <i class="fa fa-solid fa-minus text-2xs!"></i>
        </Button>

        <Button variant="ghost" size="icon" @click="selectedOption = DEFAULT_OPTION">
          <i class="far fa-text-size text-2xs!"></i>
        </Button>

        <Button variant="outline" size="icon" :disabled="disablePlus" @click="increaseSize">
          <i class="fa fa-solid fa-plus text-2xs!"></i>
        </Button>
      </div>

      <div class="flex items-center space-x-2">
        <Tooltip>
          <TooltipTrigger as-child>
            <Button variant="outline" class="w-8 h-8" @click="downloadAttachment">
              <i class="fa-solid fa-download"></i>
            </Button>
          </TooltipTrigger>
          <TooltipContent side="bottom">
            <Button variant="link" size="inline" class="text-2xs" @click="downloadAttachment">
              {{ fileName }}
            </Button>
          </TooltipContent>
        </Tooltip>

        <slot name="csv_viewer_actions"></slot>

        <Tooltip>
          <TooltipTrigger as-child>
            <Button variant="outline" class="w-8 h-8" @click="toggle">
              <i class="fa-solid fa-expand text-2xs!"></i>
            </Button>
          </TooltipTrigger>
          <TooltipContent class="text-2xs" side="bottom">View fullscreen</TooltipContent>
        </Tooltip>
      </div>
    </div>
    <div v-if="expectedCSVTypes.includes(mimeType || '')" class="flex-1 min-h-0 overflow-auto">
      <div
        class="w-full relative"
        :class="{ 'border rounded-br-sm rounded-bl-sm': !csvTableError }"
      >
        <div v-if="csvTableError" class="border-t pt-4">
          Error parsing CSV file
          <span
            class="font-medium text-blue hover:text-blue/50 cursor-pointer"
            @click="downloadAttachment"
          >
            Download {{ fileName }}
          </span>
        </div>

        <div class="bg-background w-full h-full">
          <div :class="selectedOption" v-html="csvTableHtml"></div>
        </div>
      </div>
    </div>

    <div v-else class="font-medium">
      We are unable to preview this file.
      <Button variant="link" size="inline" @click="downloadAttachment">
        Download {{ fileName }}</Button
      >
    </div>
  </div>
</template>

<style scoped>
  :fullscreen {
    display: flex;
    flex-direction: column;
    width: 100vw;
    height: 100vh;
    padding: 2rem;
    background: white;
  }

  :fullscreen .overflow-auto {
    flex: 1;
    width: 100%;
    height: 100%;
  }

  :fullscreen table thead {
    position: sticky;
    top: 0;
    background: white;
    z-index: 10;
  }

  table thead {
    position: sticky;
    top: 0;
    background: white;
    z-index: 10;
  }
</style>
